<script>
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import googleLeadConfigAPI from 'dashboard/api/googleLeadConfig';

export default {
  components: {
    NextButton,
  },
  data() {
    return {
      loading: false,
      webhookUrl: '',
      webhookKey: '',
      enabled: true,
      conversionIdentifier: '',
    };
  },
  mounted() {
    this.fetchConfig();
  },
  methods: {
    async fetchConfig() {
      this.loading = true;
      try {
        const { data } = await googleLeadConfigAPI.get();
        this.applyConfig(data);
      } catch (error) {
        // silent
      } finally {
        this.loading = false;
      }
    },
    applyConfig(data) {
      this.webhookUrl = data.webhook_url;
      this.webhookKey = data.webhook_key;
      this.enabled = data.enabled;
      this.conversionIdentifier = data.conversion_identifier || '';
    },
    async copy(value) {
      await copyTextToClipboard(value);
      useAlert(this.$t('META_ADS.LEAD_FORMS.COPIED'));
    },
    async regenerate() {
      try {
        const { data } = await googleLeadConfigAPI.regenerate();
        this.applyConfig(data);
        useAlert(this.$t('META_ADS.LEAD_FORMS.REGENERATED'));
      } catch (error) {
        useAlert(this.$t('META_ADS.LEAD_FORMS.SAVE_ERROR'));
      }
    },
    async save() {
      try {
        const { data } = await googleLeadConfigAPI.update({
          enabled: this.enabled,
          conversion_identifier: this.conversionIdentifier,
        });
        this.applyConfig(data);
        useAlert(this.$t('META_ADS.LEAD_FORMS.SAVED'));
      } catch (error) {
        useAlert(this.$t('META_ADS.LEAD_FORMS.SAVE_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ $t('META_ADS.LEAD_FORMS.HEADER') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-10">
        {{ $t('META_ADS.LEAD_FORMS.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex flex-col gap-5 p-5 rounded-xl bg-n-solid-2">
      <label class="flex flex-col gap-1 text-sm text-n-slate-11">
        {{ $t('META_ADS.LEAD_FORMS.WEBHOOK_URL') }}
        <div class="flex gap-2">
          <input
            :value="webhookUrl"
            readonly
            class="flex-1 px-3 py-2 font-mono text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
          <NextButton
            icon="i-lucide-copy"
            slate
            faded
            @click="copy(webhookUrl)"
          />
        </div>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-11">
        {{ $t('META_ADS.LEAD_FORMS.WEBHOOK_KEY') }}
        <div class="flex gap-2">
          <input
            :value="webhookKey"
            readonly
            class="flex-1 px-3 py-2 font-mono text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
          <NextButton
            icon="i-lucide-copy"
            slate
            faded
            @click="copy(webhookKey)"
          />
          <NextButton
            icon="i-lucide-refresh-cw"
            slate
            faded
            :label="$t('META_ADS.LEAD_FORMS.REGENERATE')"
            @click="regenerate"
          />
        </div>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-11">
        {{ $t('META_ADS.LEAD_FORMS.CONVERSION_IDENTIFIER') }}
        <input
          v-model="conversionIdentifier"
          type="text"
          class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          :placeholder="
            $t('META_ADS.LEAD_FORMS.CONVERSION_IDENTIFIER_PLACEHOLDER')
          "
        />
        <span class="text-xs text-n-slate-10">
          {{ $t('META_ADS.LEAD_FORMS.CONVERSION_IDENTIFIER_HINT') }}
        </span>
      </label>

      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input v-model="enabled" type="checkbox" class="rounded" />
        {{ $t('META_ADS.LEAD_FORMS.ENABLED') }}
      </label>

      <div class="flex justify-end">
        <NextButton :label="$t('META_ADS.LEAD_FORMS.SAVE')" @click="save" />
      </div>
    </div>

    <div class="flex flex-col gap-2 p-5 rounded-xl bg-n-solid-2">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ $t('META_ADS.LEAD_FORMS.SETUP.TITLE') }}
      </h2>
      <ol
        class="flex flex-col gap-1 pl-5 text-sm list-decimal text-n-slate-11 marker:text-n-slate-10"
      >
        <li>{{ $t('META_ADS.LEAD_FORMS.SETUP.STEP1') }}</li>
        <li>{{ $t('META_ADS.LEAD_FORMS.SETUP.STEP2') }}</li>
        <li>{{ $t('META_ADS.LEAD_FORMS.SETUP.STEP3') }}</li>
        <li>{{ $t('META_ADS.LEAD_FORMS.SETUP.STEP4') }}</li>
      </ol>
    </div>
  </div>
</template>
